import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material'
import AiTemplateArrayInput from './AiTemplateArrayInput'

// Data imports
import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'
import { useGlobalStoreData } from '../../../../../store/globalStore'
import { TRADE_TYPES } from '../../../utils/campaignConstants'

const EmailPurposeTargetingSection = ({
  control,
  fieldPrefix = 'emailPurpose',
  showTitle = false,
  requiredFieldsOnly = false
}) => {
  const { t } = useTranslation()
  const currentLang = useGlobalStoreData.language()

  const getCurrentCountries = useMemo(() => {
    const countries = currentLang === 'en' ? countryEN : countryTR
    return countries.sort((a, b) => a.countryName.localeCompare(b.countryName))
  }, [currentLang])

  return (
    <>
      {showTitle && (
        <Typography variant="h6" gutterBottom color="primary">
          {t('mailTemplate.wizard.ai.emailPurpose')}
        </Typography>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.target_market`}
            control={control}
            rules={{ required: t('this_field_cannot_be_left_blank') }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth required error={!!error} size="small">
                <InputLabel>{t('mailTemplate.wizard.ai.targetCountry')}</InputLabel>
                <Select
                  {...field}
                  label={t('mailTemplate.wizard.ai.targetCountry')}
                  sx={{
                    height: 40
                  }}
                >
                  {getCurrentCountries.map((country) => (
                    <MenuItem key={country.countryCode} value={country.countryName}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
                {error && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{error.message}</Typography>}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.trade_type`}
            control={control}
            rules={{ required: t('this_field_cannot_be_left_blank') }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth required error={!!error} size="small">
                <InputLabel>{t('mailTemplate.wizard.ai.tradeType')}</InputLabel>
                <Select
                  {...field}
                  label={t('mailTemplate.wizard.ai.tradeType')}
                  sx={{
                    height: 40
                  }}
                >
                  <MenuItem value={TRADE_TYPES.EXPORT}>{t('mailTemplate.wizard.ai.export')}</MenuItem>
                  <MenuItem value={TRADE_TYPES.IMPORT}>{t('mailTemplate.wizard.ai.import')}</MenuItem>
                </Select>
                {error && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{error.message}</Typography>}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.product_category`}
            control={control}
            rules={{ required: t('this_field_cannot_be_left_blank') }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('mailTemplate.wizard.ai.productCategory')}
                placeholder={t('mailTemplate.wizard.ai.placeholders.productCategory')}
                required
                error={!!error}
                helperText={error?.message}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 40
                  }
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name={`${fieldPrefix}.language`}
            control={control}
            rules={{ required: t('this_field_cannot_be_left_blank') }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth required error={!!error} size="small">
                <InputLabel>{t('mailTemplate.wizard.ai.language')}</InputLabel>
                <Select
                  {...field}
                  label={t('mailTemplate.wizard.ai.language')}
                  sx={{
                    height: 40
                  }}
                >
                  <MenuItem value="English">{t('mailTemplate.wizard.ai.languages.english')}</MenuItem>
                  <MenuItem value="Chinese">{t('mailTemplate.wizard.ai.languages.chinese')}</MenuItem>
                  <MenuItem value="Spanish">{t('mailTemplate.wizard.ai.languages.spanish')}</MenuItem>
                  <MenuItem value="Arabic">{t('mailTemplate.wizard.ai.languages.arabic')}</MenuItem>
                  <MenuItem value="French">{t('mailTemplate.wizard.ai.languages.french')}</MenuItem>
                  <MenuItem value="Portuguese">{t('mailTemplate.wizard.ai.languages.portuguese')}</MenuItem>
                  <MenuItem value="Russian">{t('mailTemplate.wizard.ai.languages.russian')}</MenuItem>
                  <MenuItem value="Japanese">{t('mailTemplate.wizard.ai.languages.japanese')}</MenuItem>
                  <MenuItem value="Vietnamese">{t('mailTemplate.wizard.ai.languages.vietnamese')}</MenuItem>
                  <MenuItem value="Italian">{t('mailTemplate.wizard.ai.languages.italian')}</MenuItem>
                  <MenuItem value="Turkish">{t('mailTemplate.wizard.ai.languages.turkish')}</MenuItem>
                  <MenuItem value="Korean">{t('mailTemplate.wizard.ai.languages.korean')}</MenuItem>
                  <MenuItem value="German">{t('mailTemplate.wizard.ai.languages.german')}</MenuItem>
                  <MenuItem value="Indonesian">{t('mailTemplate.wizard.ai.languages.indonesian')}</MenuItem>
                  <MenuItem value="Persian">{t('mailTemplate.wizard.ai.languages.persian')}</MenuItem>
                  <MenuItem value="Polish">{t('mailTemplate.wizard.ai.languages.polish')}</MenuItem>
                  <MenuItem value="Dutch">{t('mailTemplate.wizard.ai.languages.dutch')}</MenuItem>
                  <MenuItem value="Malay">{t('mailTemplate.wizard.ai.languages.malay')}</MenuItem>
                  <MenuItem value="Romanian">{t('mailTemplate.wizard.ai.languages.romanian')}</MenuItem>
              </Select>
                {error && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{error.message}</Typography>}
              </FormControl>
            )}
          />
        </Grid>

        {!requiredFieldsOnly && (
          <>
            <Grid item xs={12}>
              <Controller
                name={`${fieldPrefix}.specific_products`}
                control={control}
                render={({ field: { value = [], onChange } }) => (
                  <AiTemplateArrayInput
                    label={t('mailTemplate.wizard.ai.specificProducts')}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.specificProducts')}
                    items={value}
                    onAdd={(newValue) => {
                      if (newValue.trim()) {
                        onChange([...value, { value: newValue.trim() }])
                      }
                    }}
                    onRemove={(index) => {
                      const newValue = [...value]
                      newValue.splice(index, 1)
                      onChange(newValue)
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name={`${fieldPrefix}.call_to_action`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    label={t('mailTemplate.wizard.ai.callToAction')}
                    placeholder={t('mailTemplate.wizard.ai.placeholders.callToAction')}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default EmailPurposeTargetingSection
